import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ChatBox from '../components/ChatBox';
import './ClientDashboard.css';

const ClientDashboard = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    inProgress: 0,
    completed: 0,
    totalSpent: 0
  });
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedChatProject, setSelectedChatProject] = useState(null);
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [newProject, setNewProject] = useState({
    title: '',
    description: '',
    budget: '',
    deadline: '',
    category: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = JSON.parse(localStorage.getItem('user') || '{}');

    if (!token || userData.role !== 'client') {
      navigate('/login');
      return;
    }

    fetchProjects();
  }, [navigate]);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/projects/client-projects', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setProjects(response.data);
      
      // Calculate stats
      const totalSpent = response.data
        .filter(p => p.status === 'completed')
        .reduce((sum, p) => sum + (parseFloat(p.budget) || 0), 0);
      
      const stats = {
        total: response.data.length,
        pending: response.data.filter(p => p.status === 'pending').length,
        inProgress: response.data.filter(p => p.status === 'in-progress').length,
        completed: response.data.filter(p => p.status === 'completed').length,
        totalSpent
      };
      setStats(stats);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setLoading(false);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/projects', newProject, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowNewProjectModal(false);
      setNewProject({
        title: '',
        description: '',
        budget: '',
        deadline: '',
        category: ''
      });
      fetchProjects();
      alert('Το έργο υποβλήθηκε επιτυχώς! Ο διαχειριστής θα το εξετάσει και θα αναθέσει έναν κατάλληλο freelancer.');
    } catch (error) {
      console.error('Error creating project:', error);
      alert('Failed to create project');
    }
  };

  const getStatusClass = (status) => {
    const statusMap = {
      'pending': 'status-pending',
      'in-progress': 'status-progress',
      'completed': 'status-completed',
      'cancelled': 'status-cancelled'
    };
    return statusMap[status] || 'status-default';
  };

  const getStatusText = (status) => {
    const statusMap = {
      'pending': 'Σε αναμονή',
      'in-progress': 'Σε εξέλιξη',
      'completed': 'Ολοκληρώθηκε',
      'cancelled': 'Ακυρώθηκε'
    };
    return statusMap[status] || status;
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>Φόρτωση Dashboard...</p>
      </div>
    );
  }

  return (
    <div className="client-dashboard">
      <div className="dashboard-header">
        <h1>Πίνακας Ελέγχου Πελάτη</h1>
        <button className="btn btn-primary" onClick={() => setShowNewProjectModal(true)}>
          + Νέο Έργο
        </button>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <h3>{stats.total}</h3>
            <p>Σύνολο Έργων</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">⏳</div>
          <div className="stat-content">
            <h3>{stats.pending}</h3>
            <p>Σε Αναμονή Ανάθεσης</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🚀</div>
          <div className="stat-content">
            <h3>{stats.inProgress}</h3>
            <p>Σε Εξέλιξη</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <h3>{stats.completed}</h3>
            <p>Ολοκληρωμένα</p>
          </div>
        </div>
        <div className="stat-card stat-highlight">
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <h3>€{stats.totalSpent.toLocaleString()}</h3>
            <p>Συνολική Δαπάνη</p>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="info-banner">
        <div className="info-icon">ℹ️</div>
        <div className="info-content">
          <strong>Πώς λειτουργεί:</strong> Υποβάλετε το έργο σας και ο διαχειριστής μας θα το αναθέσει σε έναν κατάλληλο freelancer. Για οποιαδήποτε ερώτηση ή αλλαγή, επικοινωνήστε με τον διαχειριστή.
        </div>
      </div>

      {/* Projects List */}
      <div className="projects-section">
        <h2>Τα Έργα μου</h2>
        
        {projects.length === 0 ? (
          <div className="empty-state">
            <p>Δεν έχετε ακόμα έργα</p>
            <button className="btn btn-primary" onClick={() => setShowNewProjectModal(true)}>
              Δημιουργήστε το πρώτο σας έργο
            </button>
          </div>
        ) : (
          <div className="projects-list">
            {projects.map(project => (
              <div key={project._id} className="project-card">
                <div className="project-header">
                  <div>
                    <h3>{project.title}</h3>
                    <span className={`status-badge ${getStatusClass(project.status)}`}>
                      {getStatusText(project.status)}
                    </span>
                  </div>
                  <div className="project-budget">€{project.budget}</div>
                </div>
                
                <p className="project-description">{project.description}</p>
                
                <div className="project-meta">
                  <div className="meta-item">
                    <strong>Κατηγορία:</strong> {project.category}
                  </div>
                  {project.deadline && (
                    <div className="meta-item">
                      <strong>Deadline:</strong> {new Date(project.deadline).toLocaleDateString('el-GR')}
                    </div>
                  )}
                  {project.freelancer && (
                    <div className="meta-item">
                      <strong>Freelancer:</strong> {project.freelancer.name}
                    </div>
                  )}
                </div>
                
                <div className="project-actions">
                  <button 
                    className="btn btn-sm btn-secondary"
                    onClick={() => setSelectedProject(project)}
                  >
                    Λεπτομέρειες
                  </button>
                  {project.assignedTo && (
                    <button 
                      className="btn btn-sm btn-primary"
                      onClick={() => setSelectedChatProject(project)}
                    >
                      💬 Chat
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* New Project Modal */}
      {showNewProjectModal && (
        <div className="modal-overlay" onClick={() => setShowNewProjectModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Δημιουργία Νέου Έργου</h2>
              <button className="close-btn" onClick={() => setShowNewProjectModal(false)}>×</button>
            </div>
            
            <form onSubmit={handleCreateProject}>
              <div className="form-group">
                <label>Τίτλος Έργου *</label>
                <input
                  type="text"
                  value={newProject.title}
                  onChange={(e) => setNewProject({...newProject, title: e.target.value})}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Περιγραφή *</label>
                <textarea
                  value={newProject.description}
                  onChange={(e) => setNewProject({...newProject, description: e.target.value})}
                  rows="5"
                  required
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Προϋπολογισμός (€) *</label>
                  <input
                    type="number"
                    value={newProject.budget}
                    onChange={(e) => setNewProject({...newProject, budget: e.target.value})}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Κατηγορία *</label>
                  <select
                    value={newProject.category}
                    onChange={(e) => setNewProject({...newProject, category: e.target.value})}
                    required
                  >
                    <option value="">Επιλέξτε...</option>
                    <option value="Web Development">Web Development</option>
                    <option value="Mobile Development">Mobile Development</option>
                    <option value="Graphic Design">Graphic Design</option>
                    <option value="Content Writing">Content Writing</option>
                    <option value="Digital Marketing">Digital Marketing</option>
                    <option value="Video Editing">Video Editing</option>
                  </select>
                </div>
              </div>
              
              <div className="form-group">
                <label>Deadline</label>
                <input
                  type="date"
                  value={newProject.deadline}
                  onChange={(e) => setNewProject({...newProject, deadline: e.target.value})}
                />
              </div>
              
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowNewProjectModal(false)}>
                  Ακύρωση
                </button>
                <button type="submit" className="btn btn-primary">
                  Δημιουργία Έργου
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Project Details Modal */}
      {selectedProject && (
        <div className="modal-overlay" onClick={() => setSelectedProject(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedProject.title}</h2>
              <button className="close-btn" onClick={() => setSelectedProject(null)}>×</button>
            </div>
            
            <div className="project-details">
              <div className="detail-section">
                <h3>Κατάσταση</h3>
                <span className={`status-badge ${getStatusClass(selectedProject.status)}`}>
                  {getStatusText(selectedProject.status)}
                </span>
              </div>
              
              <div className="detail-section">
                <h3>Περιγραφή</h3>
                <p>{selectedProject.description}</p>
              </div>
              
              <div className="detail-row">
                <div className="detail-section">
                  <h3>Προϋπολογισμός</h3>
                  <p>€{selectedProject.budget}</p>
                </div>
                
                <div className="detail-section">
                  <h3>Κατηγορία</h3>
                  <p>{selectedProject.category}</p>
                </div>
              </div>
              
              {selectedProject.deadline && (
                <div className="detail-section">
                  <h3>Deadline</h3>
                  <p>{new Date(selectedProject.deadline).toLocaleDateString('el-GR')}</p>
                </div>
              )}
              
              {selectedProject.freelancer && (
                <div className="detail-section">
                  <h3>Freelancer</h3>
                  <p>{selectedProject.freelancer.name} ({selectedProject.freelancer.email})</p>
                </div>
              )}
              
              <div className="detail-section">
                <h3>Δημιουργήθηκε</h3>
                <p>{new Date(selectedProject.createdAt).toLocaleDateString('el-GR')}</p>
              </div>
            </div>
            
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setSelectedProject(null)}>
                Κλείσιμο
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chat Modal */}
      {selectedChatProject && (
        <div className="modal-overlay" onClick={() => setSelectedChatProject(null)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Συνομιλία για το έργο</h2>
              <button className="close-btn" onClick={() => setSelectedChatProject(null)}>×</button>
            </div>
            
            <ChatBox
              projectId={selectedChatProject.id}
              projectTitle={selectedChatProject.title}
              otherUser={{
                id: selectedChatProject.assignedTo,
                name: selectedChatProject.freelancer?.name || 'Freelancer'
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientDashboard;
